import React, { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { formatCurrency, formatDate, formatPeriod, currentPeriod, periodOptions } from "@/lib/format";
import StatusPill from "@/components/StatusPill";
import { CheckCircle2, BellRing, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function RentRoll() {
  const [period, setPeriod] = useState(currentPeriod());
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/payments?period=${period}`);
      setPayments(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [period]);

  const totals = useMemo(() => {
    const gross = payments.reduce((s, p) => s + (p.gross || 0), 0);
    const commission = payments.reduce((s, p) => s + (p.commission || 0), 0);
    const taxes = payments.reduce((s, p) => s + (p.taxes || 0), 0);
    const net = payments.reduce((s, p) => s + (p.net || 0), 0);
    const collected = payments.filter(p => p.status === "paid").reduce((s, p) => s + (p.amount_paid || 0), 0);
    return { gross, commission, taxes, net, collected, outstanding: gross - collected };
  }, [payments]);

  const markPaid = async (p) => {
    try {
      await api.put(`/payments/${p.id}`, {
        status: "paid",
        amount_paid: p.gross,
        paid_date: new Date().toISOString().slice(0, 10),
      });
      toast.success("Marked paid");
      load();
    } catch { toast.error("Failed to update"); }
  };

  const markUnpaid = async (p) => {
    try {
      await api.put(`/payments/${p.id}`, { status: "unpaid", amount_paid: 0, paid_date: null });
      load();
    } catch {}
  };

  const sendReminder = async (p) => {
    try {
      await api.post("/reminders/send", { payment_id: p.id });
      toast.success("Reminder logged");
    } catch { toast.error("Could not send reminder"); }
  };

  return (
    <Layout
      title="Rent Roll"
      subtitle={`Monthly income across all active contracts · ${formatPeriod(period)}`}
      actions={
        <div className="flex gap-2">
          <select
            value={period} onChange={(e) => setPeriod(e.target.value)}
            data-testid="rentroll-period-select"
            className="h-10 px-3 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
          >
            {periodOptions().map((p) => <option key={p} value={p}>{formatPeriod(p)}</option>)}
          </select>
          <button onClick={load} className="h-10 w-10 grid place-items-center bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition" data-testid="rentroll-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      }
    >
      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <TotalCell label="Gross" value={formatCurrency(totals.gross)} />
        <TotalCell label="Commission" value={formatCurrency(totals.commission)} />
        <TotalCell label="Taxes" value={formatCurrency(totals.taxes)} />
        <TotalCell label="Nett" value={formatCurrency(totals.net)} accent="emerald" />
        <TotalCell label="Collected" value={formatCurrency(totals.collected)} accent="emerald" />
        <TotalCell label="Outstanding" value={formatCurrency(totals.outstanding)} accent="red" />
      </div>

      {payments.length === 0 ? (
        <div className="card-flat p-10 text-center fade-up">
          <div className="font-display text-lg font-semibold">Nothing on the roll yet</div>
          <p className="text-sm text-slate-500 mt-1">Create a contract to auto-populate this month&apos;s payments.</p>
        </div>
      ) : (
        <div className="card-flat overflow-x-auto fade-up">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left px-6 py-3 font-medium">Tenant</th>
                <th className="text-left px-6 py-3 font-medium">Property</th>
                <th className="text-left px-6 py-3 font-medium">Due</th>
                <th className="text-right px-6 py-3 font-medium">Gross</th>
                <th className="text-right px-6 py-3 font-medium">Commission</th>
                <th className="text-right px-6 py-3 font-medium">Taxes</th>
                <th className="text-right px-6 py-3 font-medium">Nett</th>
                <th className="text-left px-6 py-3 font-medium">Status</th>
                <th className="text-right px-6 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {payments.map((p) => (
                <tr key={p.id} className={`hover:bg-slate-50/50 ${p.status !== "paid" && p.period < currentPeriod() ? "bg-red-50/30" : ""}`} data-testid={`payment-row-${p.id}`}>
                  <td className="px-6 py-3 font-medium">{p.tenant_name}</td>
                  <td className="px-6 py-3 text-slate-700">{p.property_name}</td>
                  <td className="px-6 py-3 text-slate-700">{formatDate(p.due_date)}</td>
                  <td className="px-6 py-3 text-right font-mono-plex">{formatCurrency(p.gross)}</td>
                  <td className="px-6 py-3 text-right font-mono-plex text-slate-500">−{formatCurrency(p.commission)}</td>
                  <td className="px-6 py-3 text-right font-mono-plex text-slate-500">−{formatCurrency(p.taxes)}</td>
                  <td className="px-6 py-3 text-right font-mono-plex font-medium">{formatCurrency(p.net)}</td>
                  <td className="px-6 py-3">
                    <StatusPill status={p.status === "paid" ? "paid" : (p.period < currentPeriod() ? "overdue" : "unpaid")} />
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      {p.status === "paid" ? (
                        <button onClick={() => markUnpaid(p)} data-testid={`payment-unpaid-${p.id}`} className="text-xs px-2.5 py-1 rounded-md border border-slate-200 hover:bg-slate-50">Undo</button>
                      ) : (
                        <>
                          <button onClick={() => markPaid(p)} data-testid={`payment-paid-${p.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors">
                            <CheckCircle2 className="w-3 h-3" /> Paid
                          </button>
                          <button onClick={() => sendReminder(p)} data-testid={`payment-remind-${p.id}`} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-slate-200 hover:bg-slate-50 transition-colors">
                            <BellRing className="w-3 h-3" /> Remind
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
}

function TotalCell({ label, value, accent }) {
  const color = accent === "emerald" ? "text-emerald-700" : accent === "red" ? "text-red-700" : "text-slate-900";
  return (
    <div className="card-flat p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-display text-lg font-semibold mt-1 ${color}`}>{value}</div>
    </div>
  );
}
